import { fetchWithAuth } from "../../../common/fetchWithAuth";
import { APIConstants, ChatConstants } from "../../../utilities/Constants";

/**
 * ChatMS client - Remi, the AI billing assistant.
 *
 * Implements the contract in ChatMS/ChatMS/docs/Chat.md (branch 0806-ai-chatbot-ms).
 * Field names below match the service exactly (PascalCase with s/i/b type prefixes) -
 * do not "tidy" them into camelCase, they are the wire format.
 *
 * Phase 1 is read-only and NON-STREAMING: the service runs an agent loop that may call
 * several billing APIs before it can answer, so a single response arrives after 5-20s.
 *
 * Auth: fetchWithAuth attaches the biller's bearer token and handles the silent
 * 401-refresh. The token is not just endpoint authorisation - it is what authorises
 * the downstream billing reads, so ChatMS sees exactly what the biller can see.
 */

/** Client timeout. The service can legitimately take 20s+; 60s avoids false failures. */
const REQUEST_TIMEOUT_MS = 60000;

function joinUrl(base, path) {
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
}

/** Carries a user-facing message plus the classification the UI needs to react. */
export class ChatError extends Error {
  constructor(message, { status = 0, kind = "error" } = {}) {
    super(message);
    this.name = "ChatError";
    this.status = status;
    this.kind = kind; // "unavailable" | "auth" | "mismatch" | "notFound" | "validation" | "error"
  }
}

/**
 * Maps ChatMS status codes to what the biller should actually see.
 * Per the contract, 400 bodies are written for humans, so we surface them verbatim.
 */
async function toChatError(res) {
  let body = "";
  try {
    body = (await res.text()).slice(0, 400);
  } catch {
    /* empty or already consumed */
  }

  switch (res.status) {
    case 400:
      return new ChatError(body || "That request wasn't valid.", { status: 400, kind: "validation" });
    case 401:
      // fetchWithAuth already retried once after a refresh; reaching here means re-login.
      return new ChatError("Your session has expired. Please sign in again.", { status: 401, kind: "auth" });
    case 403:
      return new ChatError("Session mismatch — please sign in again.", { status: 403, kind: "mismatch" });
    case 404:
      return new ChatError("Conversation not found.", { status: 404, kind: "notFound" });
    case 503:
      return new ChatError("The assistant is unavailable.", { status: 503, kind: "unavailable" });
    default:
      if (res.status >= 500) {
        return new ChatError("Something went wrong. Please try again.", { status: res.status, kind: "error" });
      }
      return new ChatError(body || `Request failed (${res.status}).`, { status: res.status, kind: "error" });
  }
}

async function request(path, { method = "GET", body, query, signal } = {}) {
  let url = joinUrl(APIConstants.BASE_URL_CHAT, path);
  if (query) {
    const qs = new URLSearchParams(
      Object.entries(query).filter(([, v]) => v !== null && v !== undefined && v !== "")
    ).toString();
    if (qs) url += `?${qs}`;
  }

  // Combine the caller's abort signal with our own timeout.
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);
  const onCallerAbort = () => timeoutController.abort();
  signal?.addEventListener("abort", onCallerAbort);

  let res;
  try {
    res = await fetchWithAuth(url, {
      method,
      signal: timeoutController.signal,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    if (signal?.aborted) throw Object.assign(new Error("Aborted"), { name: "AbortError" });
    if (timeoutController.signal.aborted) {
      throw new ChatError("That took too long. Try narrowing the question to one claim or a shorter date range.", {
        kind: "error",
      });
    }
    throw new ChatError(
      "Remi's service isn't reachable. If ChatMS isn't running yet, that's expected.",
      { kind: "unavailable" }
    );
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }

  if (!res.ok) throw await toChatError(res);
  if (res.status === 204) return null;
  return res.json();
}

/**
 * Ask a question.
 *
 * @param {object}  args
 * @param {?number} args.conversationID  null starts a new conversation
 * @param {number}  args.practiceID      MUST come from the Redux user object - ChatMS
 *                                       compares it against the token claim and 403s on mismatch
 * @param {number}  args.userID
 * @param {string}  args.message
 * @param {object}  args.pageContext     what's on screen (see usePageContext)
 * @param {object}  args.sessionContext  role/template/timezone/location from Redux
 * @returns {Promise<object>} the raw ChatMS response
 */
export function sendMessage({
  conversationID = null,
  practiceID,
  userID,
  message,
  pageContext,
  sessionContext,
  signal,
}) {
  return request(ChatConstants.SendMessage, {
    method: "POST",
    signal,
    body: {
      ConversationID: conversationID,
      PracticeID: practiceID,
      UserID: userID,
      sMessage: message,
      PageContext: pageContext,
      SessionContext: sessionContext,
    },
  });
}

/**
 * Personalised starter prompts, ranked from which tools this biller actually uses.
 *
 * Deliberately NOT wrapped in the shared error handling: this endpoint is an
 * enhancement, never a dependency. A failure here must be invisible - the widget
 * falls back to its static list and opens exactly as fast. Never let a suggestions
 * call block or break the panel.
 */
export async function fetchSuggestions({ practiceID, userID, signal } = {}) {
  try {
    return await request(ChatConstants.GetSuggestions, {
      method: "GET",
      signal,
      query: { PracticeID: practiceID, UserID: userID },
    });
  } catch {
    return null;
  }
}

/** Lists this user's conversations, newest first. */
export function getConversations({ practiceID, userID, signal } = {}) {
  return request(ChatConstants.GetConversations, {
    method: "GET",
    signal,
    query: { PracticeID: practiceID, UserID: userID },
  });
}

/** Loads one thread's full history, including citations and data sources. */
export function getConversation({ conversationID, practiceID, userID, signal } = {}) {
  return request(ChatConstants.GetConversation, {
    method: "GET",
    signal,
    query: { ConversationID: conversationID, PracticeID: practiceID, UserID: userID },
  });
}

/**
 * Records a thumbs up/down. iRating accepts only 1 or -1.
 * Fire-and-forget: never blocks or fails the UI.
 */
export async function saveFeedback({ conversationID, messageID, practiceID, userID, rating, comment = "" }) {
  try {
    await request(ChatConstants.SaveFeedback, {
      method: "POST",
      body: {
        ConversationID: conversationID,
        MessageID: messageID,
        PracticeID: practiceID,
        UserID: userID,
        iRating: rating > 0 ? 1 : -1,
        sComment: comment.slice(0, 2000),
      },
    });
    return true;
  } catch {
    return false;
  }
}
